import { type PostHog, type PostHogConfig } from 'posthog-js'
import type { User } from 'firebase/auth'
import { kProduction } from '@/constants/constants-ui'
import { ApiError } from '@/util/errors/api-error'
import { FlagSource, getRuntimeFlags } from './fetch/get-runtime-flags'
import { FeatureFlagLoadState } from './context'

// Public tokens identify the in-memory SDK; the browser sends no PostHog requests.
export const posthogProjectToken = kProduction ? 'phc_pKmJwPYTfbsUba2Nd5K4ha9JEdnNE3jv4e8Qtnm4Cdqj' : 'phc_ywqiNgbDpKT2hyXw3hkJgBwmbHGcedxtFVZEtee7pwTs'
export const featureFlagRefreshMs = 30_000
// Preserve the existing initial-loading budget for our authenticated API request.
export const featureFlagRequestTimeoutMs = 6_500

export const featureFlagClientOptions: Partial<PostHogConfig> = {
  api_host: 'https://us.i.posthog.com',
  persistence: 'memory',
  person_profiles: 'never',
  ip: false,
  save_referrer: false,
  save_campaign_params: false,
  autocapture: false,
  capture_pageview: false,
  capture_pageleave: false,
  capture_dead_clicks: false,
  capture_performance: false,
  capture_exceptions: false,
  disable_session_recording: true,
  disable_surveys: true,
  disable_web_experiments: true,
  enable_heatmaps: false,
  disable_external_dependency_loading: true,
  advanced_disable_feature_flags: true,
  advanced_disable_flags: true,
  // Exposure/outcome instrumentation is a separate decision when an experiment needs it.
  before_send: () => null,
}

/** One identity owns requests, SDK state, and the refresh lifecycle. */
export function startFeatureFlagClient(client: PostHog, user: User, onLoadState: (state: FeatureFlagLoadState) => void): () => void {
  client.init(posthogProjectToken, {
    ...featureFlagClientOptions,
    bootstrap: { distinctID: user.uid, isIdentifiedID: false },
  })
  let stopped = false
  // Set while the API rejects this identity (e.g. unpaid organization). Timed refreshes stop;
  // returning to the tab or reconnecting retries in case access was restored.
  let rejected = false
  // The first attempt always runs so loading settles; later ones wait for a visible, online tab.
  let attempted = false
  let source: FlagSource | undefined
  let request: AbortController | undefined
  let refreshTimer: number | undefined

  async function refresh() {
    if (stopped || request || source === FlagSource.CHECKED_IN) return
    if (attempted && (document.visibilityState === 'hidden' || !navigator.onLine)) return
    window.clearTimeout(refreshTimer)
    const controller = new AbortController()
    request = controller
    const deadline = window.setTimeout(() => controller.abort(), featureFlagRequestTimeoutMs)
    try {
      const result = await getRuntimeFlags(user, controller.signal)
      if (stopped) return
      source = result.source
      rejected = false
      client.updateFlags(result.flags)
      onLoadState(FeatureFlagLoadState.READY)
    } catch (error) {
      if (stopped) return
      rejected = error instanceof ApiError && [401, 402, 403].includes(error.status)
      if (rejected) {
        // A rejected identity must not keep earlier decisions; frontend defaults apply.
        client.updateFlags({})
        onLoadState(FeatureFlagLoadState.UNAVAILABLE)
      } else if (!source) {
        onLoadState(FeatureFlagLoadState.UNAVAILABLE)
      }
      // Other refresh failures keep earlier decisions, including explicit OFF values.
    } finally {
      attempted = true
      window.clearTimeout(deadline)
      request = undefined
      if (!stopped && !rejected && source !== FlagSource.CHECKED_IN) refreshTimer = window.setTimeout(refresh, featureFlagRefreshMs)
    }
  }

  const resume = () => {
    if (document.visibilityState !== 'hidden' && navigator.onLine) void refresh()
  }
  document.addEventListener('visibilitychange', resume)
  window.addEventListener('online', resume)
  void refresh()

  return () => {
    stopped = true
    window.clearTimeout(refreshTimer)
    request?.abort()
    document.removeEventListener('visibilitychange', resume)
    window.removeEventListener('online', resume)
    client.reset(true)
    void client.shutdown()
  }
}
